import { ClinicAuthError } from "@auth/clinicAuthClient";
import { notificationsKeys } from "@notifications/notificationsQueries";
import { pimsActivityKeys } from "@pims/pimsActivityQueries";
import { useQueryClient } from "@tanstack/react-query";
import { submitTaskAction, type Task } from "@tasks/tasksClient";
import { tasksKeys } from "@tasks/tasksQueries";
import { useCallback, useState } from "react";

/**
 * Shared task-action handling: submit an a2ui action to `PATCH /tasks` and
 * hide answered items optimistically until the refetch catches up. Every
 * action resolves clinic-wide, dismiss included (BE-1877); only alert cards
 * expose a ✕, so a dismiss can't bury an open question.
 */
export const useTaskActions = () => {
  const queryClient = useQueryClient();
  // Optimistically hide a task the moment it's answered, before the refetch
  // (which drops it once the backend stamps `submitted_action`) catches up.
  const [answered, setAnswered] = useState<Set<string>>(new Set());

  const refetch = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: tasksKeys.all }),
      queryClient.invalidateQueries({ queryKey: notificationsKeys.all }),
      // Resolving a task drops it from any open view's per-view task list/bell
      // and decrements the `openTaskCount` on view cards.
      queryClient.invalidateQueries({ queryKey: ["view"] }),
      queryClient.invalidateQueries({ queryKey: ["views"] }),
      // A pims_write card submit settles hans_tasks rows server-side
      // (backend BE-2377): the activity view's staff bucket and the settled
      // history both move, so they refetch with the task lists.
      queryClient.invalidateQueries({ queryKey: pimsActivityKeys.all }),
    ]);

  const handleAction = async (
    task: Task,
    action: string,
    data: Record<string, unknown>,
  ) => {
    setAnswered((prev) => new Set(prev).add(task.id));
    try {
      await submitTaskAction(task.id, action, data);
      await refetch();
    } catch (error) {
      // 409 = someone else answered first. The question is resolved, so let the
      // refetch show the task already done — no error to surface.
      if (error instanceof ClinicAuthError && error.status === 409) {
        await refetch();
        return;
      }
      throw error;
    } finally {
      // Clear the hide once the refetch has landed: terminal actions have
      // dropped the task from the feed, while ask_changes keeps it open with a
      // new pending payload that must re-appear. A failure re-shows the card to
      // retry.
      setAnswered((prev) => {
        const next = new Set(prev);
        next.delete(task.id);
        return next;
      });
    }
  };

  // Hides items just answered here, which the server has already completed.
  const isAnswered = useCallback((id: string) => answered.has(id), [answered]);

  return { handleAction, isAnswered };
};
